import { Maybe } from '@passionware/monads';
import { isUrlValid } from './urlHelpers';

/**
 * Abstract interface for link resolvers
 * A link resolver takes an input string and returns structured information
 */
export type AbstractLinkResolver = {
  /**
   * Parse the input string and return the platform and handle
   * @param inputString - The input string to parse
   * @returns The platform and handle
   */
  parseInput: (inputString: string) => Maybe<AbstractLinkParseResult>;

  /**
   * Get matching score for the input string (0-1 scale)
   * 0 = definitely not this platform
   * 1 = definitely this platform
   * @param inputString - The input string to score
   * @returns Score between 0 and 1
   */
  getMatchingScore: (inputString: string) => number;
};

/**
 * Result of parsing a social media link
 */
export type AbstractLinkParseResult = {
  handle: string;
  fullUrl: string;
  /**
   * Optional React node for displaying handle with icon (e.g., LinkedIn with User/Building icon)
   */
  richHandle?: React.ReactNode;
};

/**
 * Options for creating a platform-aware parser
 */
export type PlatformAwareParserOptions = {
  /**
   * Platform base URL (e.g., 'instagram.com/', 'discord.com/users/')
   * If empty, the parser will treat all input as-is (for 'other' platform)
   */
  platformBase: string;

  /**
   * Function to parse a full URL when platform base is detected
   * @param inputString - The full URL input
   * @param normalizedInput - The normalized (lowercase) input
   * @param platformBase - The platform base URL
   * @param normalizedBase - The normalized platform base URL
   * @returns Parsed result or null if URL is invalid
   */
  parseFullUrl: (
    inputString: string,
    normalizedInput: string,
    platformBase: string,
    normalizedBase: string
  ) => Maybe<AbstractLinkParseResult>;

  /**
   * Function to parse a handle (non-URL input)
   * @param inputString - The handle input
   * @param normalizedInput - The normalized (lowercase) input
   * @returns Parsed result
   */
  parseHandle: (
    inputString: string,
    normalizedInput: string
  ) => AbstractLinkParseResult;

  /**
   * When set, host-shaped input without `http(s)://` and without `platformBase` may still be parsed
   * as a handle if `maxInputScoring` is positive (e.g. Instagram dotted usernames).
   */
  inputScoring?: Maybe<Map<RegExp, number>>;
};

/**
 * Options for creating a platform-aware scorer
 */
export type PlatformAwareScorerOptions = {
  /**
   * Platform base URL (e.g., 'instagram.com/', 'discord.com/users/')
   * If empty, returns lowest score (0.1) for 'other' platform
   */
  platformBase: string;

  /**
   * Regex → score. The **first capturing group** (when present) is the extracted handle; if a
   * pattern has no group, the full match is the handle. Score is the max among matching patterns;
   * on a tie, the earlier Map entry wins.
   */
  inputScoring: Maybe<Map<RegExp, number>>;
};

/**
 * Creates a reusable parser that handles the common control flow:
 * - Detects if input is a full URL or handle
 * - Validates platform base for full URLs
 * - Delegates to specific parsing functions
 *
 * @param options - Configuration options for the parser
 * @returns A parseInput function
 */
export const createPlatformAwareParser = (
  options: PlatformAwareParserOptions
) => {
  return (inputString: string): Maybe<AbstractLinkParseResult> => {
    const { platformBase, parseFullUrl, parseHandle, inputScoring } = options;

    // Skip parsing for empty platformBase (e.g., 'other' platform)
    if (!platformBase) {
      return null;
    }

    // Check whether input looks like a URL (with or without protocol)
    const isValidUrl = isUrlValid(inputString);
    const normalizedInput = inputString.toLowerCase();
    const normalizedBase = platformBase.toLowerCase();
    const hasPlatformBase = normalizedInput.includes(normalizedBase);

    // Input starts with http:// or https://:
    // treat it as an explicit URL and accept only links for this platform.
    if (hasHttpOrHttpsProtocol(inputString)) {
      if (!hasPlatformBase) {
        // This is URL for different platform
        return null;
      }
      // Platform URL recognized, delegate to parseFullUrl
      return parseFullUrl(
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      );
    }

    // Input has no protocol but still looks like a URL and already contains this platform base
    // (e.g. "instagram.com/foo"), so parse it as a full platform URL.
    if (isValidUrl && hasPlatformBase) {
      return parseFullUrl(
        inputString,
        normalizedInput,
        platformBase,
        normalizedBase
      );
    }

    if (isValidUrl && !hasPlatformBase) {
      const scoring = inputScoring ?? null;
      if (scoring && scoring.size > 0) {
        const score = maxInputScoring(inputString, scoring);
        if (score > 0) {
          return parseHandle(inputString, normalizedInput);
        }
      }
      return null;
    }

    return parseHandle(inputString, normalizedInput);
  };
};

/**
 * Creates a reusable scorer that handles the common control flow:
 * - Detects if input is a full URL or handle
 * - Validates platform base for full URLs
 * - Scores based on input regex map
 *
 * @param options - Configuration options for the scorer
 * @returns A getMatchingScore function
 */
export const createPlatformAwareScorer = (
  options: PlatformAwareScorerOptions
) => {
  return (inputString: string): number => {
    const { platformBase, inputScoring } = options;

    if (!platformBase) {
      // 'other' platform - lowest score
      return 0.1;
    }

    const isValidUrl = isUrlValid(inputString);
    const normalizedInput = inputString.toLowerCase();
    const normalizedBase = platformBase.toLowerCase();
    const hasPlatformBase = normalizedInput.includes(normalizedBase);

    // Input starts with http:// or https://:
    // do not use broad (unanchored) regex heuristics to avoid false positives inside long URLs.
    if (hasHttpOrHttpsProtocol(inputString)) {
      if (hasPlatformBase) {
        return 1;
      }
      if (inputScoring && inputScoring.size > 0) {
        const anchored = maxAnchoredInputScoring(inputString, inputScoring);
        if (anchored > 0) {
          return anchored;
        }
      }
      return 0;
    }

    // Input has no protocol:
    // first prefer direct platform URL matches (e.g. "instagram.com/foo"),
    // then fallback to inputScoring heuristics (e.g. "ig:foo", "john.doe").
    if (isValidUrl && hasPlatformBase) {
      return 1;
    }

    if (inputScoring && inputScoring.size > 0) {
      const maxScore = maxInputScoring(inputString, inputScoring);
      if (maxScore > 0) {
        return maxScore;
      }
    }

    if (isValidUrl && !hasPlatformBase) {
      return 0;
    }

    // Plain handle without prefix - possible but uncertain
    return 0.3;
  };
};

const cloneRegexWithoutGlobal = (pattern: RegExp): RegExp =>
  new RegExp(pattern.source, pattern.flags.replace(/g/g, ''));

/**
 * Highest score among all map entries whose pattern matches the input.
 */
export function maxInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): number {
  let best = 0;
  for (const [pattern, score] of scoring) {
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      best = Math.max(best, score);
    }
  }
  return best;
}

/**
 * Like `maxInputScoring`, but only patterns whose source starts with `^` (anchored). Safe to use on
 * `https://…` strings so unanchored heuristics do not match substrings inside the URL.
 */
export function maxAnchoredInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): number {
  let best = 0;
  for (const [pattern, score] of scoring) {
    if (!pattern.source.startsWith('^')) {
      continue;
    }
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      best = Math.max(best, score);
    }
  }
  return best;
}

const hasHttpOrHttpsProtocol = (input: string): boolean =>
  /^https?:\/\//i.test(input.trim());

/**
 * Picks the highest-scoring pattern that matches `inputString` (ties → first in Map order),
 * then returns the first capture group if the regex defines one, otherwise the full match.
 * If nothing matches or `scoring` is empty, returns the trimmed input.
 */
export function extractHandleFromInputScoring(
  inputString: string,
  scoring: Map<RegExp, number>
): string {
  if (!scoring.size) {
    return inputString.trim();
  }

  let bestScore = -1;
  let bestPattern: RegExp | null = null;

  for (const [pattern, score] of scoring) {
    const testRe = cloneRegexWithoutGlobal(pattern);
    if (testRe.test(inputString)) {
      if (score > bestScore) {
        bestScore = score;
        bestPattern = pattern;
      }
    }
  }

  if (bestPattern === null) {
    return inputString.trim();
  }

  const execRe = cloneRegexWithoutGlobal(bestPattern);
  const m = execRe.exec(inputString);
  if (!m) {
    return inputString.trim();
  }

  if (m.length > 1 && m[1] !== undefined) {
    return m[1];
  }

  return m[0];
}
